/* eslint-disable react-refresh/only-export-components */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PRODUCTS as LOCAL_PRODUCTS } from '../data/products'
import { useCatalog } from './CatalogContext'

const CART_STORAGE_KEY = 'ng-shop-cart'
const CartContext = createContext(null)

function readStoredCart() {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((line) => {
        const product = LOCAL_PRODUCTS.find((item) => item.id === line.id)
        if (!product) return null
        const quantity = Math.max(1, Math.min(Number(line.quantity) || 1, product.stock))
        return { id: product.id, quantity }
      })
      .filter(Boolean)
  } catch {
    return []
  }
}

export function CartProvider({ children }) {
  const { getProductById, shopEnabled } = useCatalog()
  const resolveProduct = useCallback((id) => (
    getProductById(id) || LOCAL_PRODUCTS.find((item) => item.id === id) || null
  ), [getProductById])
  const [lines, setLines] = useState(() => readStoredCart())
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(lines))
  }, [lines])

  const openCart = useCallback(() => {
    if (!shopEnabled) return
    setIsOpen(true)
  }, [shopEnabled])
  const closeCart = useCallback(() => setIsOpen(false), [])
  const toggleCart = useCallback(() => {
    if (!shopEnabled) return
    setIsOpen((value) => !value)
  }, [shopEnabled])

  const addItem = useCallback((productId, quantity = 1) => {
    if (!shopEnabled) return false
    const product = resolveProduct(productId)
    if (!product || product.stock <= 0) return false

    setLines((current) => {
      const existing = current.find((line) => line.id === productId)
      if (existing) {
        return current.map((line) =>
          line.id === productId
            ? { ...line, quantity: Math.min(line.quantity + quantity, product.stock) }
            : line,
        )
      }
      return [...current, { id: productId, quantity: Math.min(quantity, product.stock) }]
    })
    setIsOpen(true)
    return true
  }, [resolveProduct, shopEnabled])

  const setQuantity = useCallback((productId, quantity) => {
    const product = resolveProduct(productId)
    if (!product) return

    setLines((current) => {
      if (quantity <= 0) return current.filter((line) => line.id !== productId)
      return current.map((line) =>
        line.id === productId
          ? { ...line, quantity: Math.min(quantity, product.stock) }
          : line,
      )
    })
  }, [resolveProduct])

  const removeItem = useCallback((productId) => {
    setLines((current) => current.filter((line) => line.id !== productId))
  }, [])

  const clearCart = useCallback(() => setLines([]), [])

  const items = useMemo(() => {
    return lines
      .map((line) => {
        const product = resolveProduct(line.id)
        if (!product) return null
        return {
          ...product,
          quantity: line.quantity,
          lineTotal: product.price * line.quantity,
        }
      })
      .filter(Boolean)
  }, [lines, resolveProduct])

  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  )

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.lineTotal, 0),
    [items],
  )

  const value = useMemo(
    () => ({
      items,
      itemCount,
      subtotal,
      isOpen: shopEnabled && isOpen,
      openCart,
      closeCart,
      toggleCart,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
    }),
    [
      items,
      itemCount,
      subtotal,
      isOpen,
      shopEnabled,
      openCart,
      closeCart,
      toggleCart,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
    ],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within CartProvider')
  }
  return context
}
